import React, { useEffect, useRef, useCallback } from "react";
import {
  SelectTabData,
  SelectTabEvent,
  Subtitle1,
  Tab,
  TabList,
  TabValue,
} from "@fluentui/react-components";
import { useLoaderData } from "react-router-dom";
import { MenuListLoaderData } from "./menuListLoader";
import {
  HelperUtils,
  MenuCategory,
  appInsightsEvents,
  trackEvent,
  useAppStore,
} from "../../../common";
import { useStyles } from "./MenuListStyles";
import { MenuCategoryCard } from "../MenuCategoryCard";

export const MenuList = () => {
  const styles = useStyles();
  const [context] = useAppStore((state) => [state.context]);
  const appInsights = useAppStore((state) => state.appInsights);
  const [menuListState, setSelectedCategory] = useAppStore((state) => [
    state.menuList,
    state.menuList.setSelectedCategory,
  ]);

  useEffect(() => {
    trackEvent(appInsights, appInsightsEvents.Usage.ViewComponent, context, {
      componentName: "MenuList",
    });
  }, [appInsights, context]);

  //Get data from loader data.
  const loaderData = useLoaderData() as MenuListLoaderData;
  const { menus, station } = loaderData;

  // Values for category tabs
  const [selectedTabId, setSelectedTabId] = React.useState<TabValue>("");
  const [menuCategories, setMenuCategories] = React.useState<MenuCategory[]>([]);
  const categoryRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const [tabCategory, setTabCategory] = React.useState<TabValue>("");
  const totalItems = menuCategories.reduce((total, category) => total + category.items.length, 0);
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const [windowWidth, setWindowWidth] = React.useState(window.innerWidth);

  const headerOffset = windowWidth <= 499 ? 184 : 20;
  const menuCategoryOffset = windowWidth <= 499 ? 184 : 220;

  const scrollToCategory = (menuCategoryElement: HTMLDivElement | null) => {
    if (menuCategoryElement && scrollContainerRef.current) {
      const elementPosition =
        menuCategoryElement.getBoundingClientRect().top + scrollContainerRef.current.scrollTop;
      const offsetPosition = elementPosition - menuCategoryOffset;
      scrollContainerRef.current.scrollTo({
        top: offsetPosition,
        behavior: "auto",
      });
    }
  };
  // Handle scroll and select the category upon scrolling
  const handleScroll = useCallback(() => {
    let visibleCategory: string | null = null;

    Object.entries(categoryRefs.current).forEach(([id, element]) => {
      if (element) {
        const { top, bottom } = element.getBoundingClientRect();
        const containerTop = scrollContainerRef.current!.getBoundingClientRect().top;
        if (top <= headerOffset + containerTop && bottom >= headerOffset + containerTop) {
          visibleCategory = id;
        }
      }
    });

    if (visibleCategory && visibleCategory !== menuListState.selectedCategory) {
      setSelectedCategory(visibleCategory);
      setSelectedTabId(visibleCategory);
      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }
    }
  }, [totalItems, menuListState.selectedCategory]);

  useEffect(() => {
    if (scrollContainerRef.current) {
      handleScroll();
    }

    const handleResize = () => {
      setWindowWidth(window.innerWidth);
      handleScroll();
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [handleScroll, windowWidth]);

  useEffect(() => {
    handleScroll();
    const menuItemscontainer = scrollContainerRef.current;
    if (menuItemscontainer) {
      menuItemscontainer.addEventListener("scroll", handleScroll);
    }

    return () => {
      if (menuItemscontainer) {
        menuItemscontainer.removeEventListener("scroll", handleScroll);
      }
    };
  }, [handleScroll]);
  useEffect(() => {
    if (station && menus && menus.length > 0) {
      const categories = HelperUtils.getInstance().getFilteredGroupedCafeMenusForSelectedDate(
        menus,
        station?.name as string,
      );
      categories.sort((a, b) => a.name.localeCompare(b.name));
      setMenuCategories(categories);
      setSelectedTabId(categories[0]?.name);
      document.title = `${station?.name} - Microsoft Dining`;
    }
  }, [menus, station]);

  useEffect(() => {
    // Restore previously selected category from store
    const selectedValue = menuListState.selectedCategory;
    if (selectedValue && selectedValue !== null) {
      setSelectedTabId(selectedValue);
      setTabCategory(selectedValue);
      const menuCategoryElement = categoryRefs.current[selectedValue];
      scrollToCategory(menuCategoryElement);
    }
  }, [tabCategory]);

  //Handle tab selection and scroll to the selected category
  const onTabSelect = (event: SelectTabEvent, data: SelectTabData) => {
    const selectedValue = data.value as string;
    setSelectedCategory(selectedValue); //Store the category on selection
    setSelectedTabId(data.value);
    const menuCategoryElement = categoryRefs.current[selectedValue];

    scrollToCategory(menuCategoryElement);
  };

  return (
    <div data-testid="menuList" className={styles.container}>
      <main className={styles.main} ref={windowWidth <= 499 ? scrollContainerRef : null}>
        <div className={styles.subMainContainer}>
          <div className={styles.tabListContainer}>
            <div className={styles.stationName}>
              <Subtitle1>{station?.name as string}</Subtitle1>
            </div>
            <TabList
              className={styles.tabList}
              size="medium"
              selectedValue={selectedTabId}
              onTabSelect={onTabSelect}
            >
              {menuCategories?.map((menuCategory) => {
                return (
                  <Tab value={menuCategory.name} key={menuCategory.id}>
                    {menuCategory.name}
                  </Tab>
                );
              })}
            </TabList>
          </div>
          <div
            className={styles.menuCategoriesContainer}
            ref={windowWidth > 499 ? scrollContainerRef : null}
          >
            {menuCategories?.map((menuCategory, index) => {
              const isLastCategory = index === menuCategories.length - 1;
              return (
                <div
                  key={menuCategory.id}
                  id={menuCategory.id}
                  ref={(el) => (categoryRefs.current[menuCategory.name] = el)}
                  className={isLastCategory ? styles.lastCategory : styles.menuCategoryCard}
                >
                  <MenuCategoryCard category={menuCategory} />
                </div>
              );
            })}
          </div>
        </div>
      </main>
    </div>
  );
};
